import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { Check } from 'lucide-react'
import type { ReactNode } from 'react'
import { cn } from './cn'

type MenuItem =
  | { kind: 'item'; label: ReactNode; onSelect?: () => void; disabled?: boolean; danger?: boolean; rightSlot?: ReactNode }
  | { kind: 'check'; label: ReactNode; checked: boolean; onCheckedChange: (v: boolean) => void }
  | { kind: 'separator' }
  | { kind: 'label'; label: ReactNode }

export function Menu({
  trigger,
  items,
  align = 'start',
}: {
  trigger: ReactNode
  items: MenuItem[]
  align?: 'start' | 'center' | 'end'
}) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align={align}
          sideOffset={6}
          className="z-50 min-w-[180px] rounded-lg border border-border bg-bg-elevated p-1 shadow-lg data-[state=open]:animate-in fade-in"
        >
          {items.map((it, i) => {
            if (it.kind === 'separator') {
              return <DropdownMenu.Separator key={i} className="my-1 h-px bg-border" />
            }
            if (it.kind === 'label') {
              return (
                <DropdownMenu.Label key={i} className="px-2 py-1.5 text-[11px] uppercase tracking-wider text-fg-subtle">
                  {it.label}
                </DropdownMenu.Label>
              )
            }
            if (it.kind === 'check') {
              return (
                <DropdownMenu.CheckboxItem
                  key={i}
                  checked={it.checked}
                  onCheckedChange={it.onCheckedChange}
                  className={menuItemClass()}
                >
                  <DropdownMenu.ItemIndicator className="mr-1.5 inline-flex w-3">
                    <Check size={12} />
                  </DropdownMenu.ItemIndicator>
                  <span className="flex-1">{it.label}</span>
                </DropdownMenu.CheckboxItem>
              )
            }
            return (
              <DropdownMenu.Item
                key={i}
                disabled={it.disabled}
                onSelect={it.onSelect}
                className={cn(menuItemClass(), it.danger && 'text-danger focus:bg-danger/10 focus:text-danger')}
              >
                <span className="flex-1">{it.label}</span>
                {it.rightSlot && <span className="ml-3 text-fg-subtle">{it.rightSlot}</span>}
              </DropdownMenu.Item>
            )
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

function menuItemClass(): string {
  return [
    'flex items-center px-2 py-1.5 text-sm rounded-md outline-none cursor-pointer',
    'data-[highlighted]:bg-bg-muted data-[highlighted]:text-fg',
    'data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed',
  ].join(' ')
}
